/**
 * Consentement aux témoins de pistage — Loi 25 (Québec).
 *
 * ⚠️ RÈGLE CENTRALE : rien ne se charge tant que le visiteur n'a pas dit OUI.
 * L'absence de réponse vaut REFUS, jamais acceptation. C'est l'inverse du
 * réflexe habituel (« en continuant, vous acceptez ») qui, lui, ne vaut rien :
 * la loi exige un consentement libre, éclairé et donné pour une fin précise.
 *
 * L'obligation suit l'entreprise, pas le visiteur : Roof Border exerce ses
 * activités au Québec, donc la règle s'applique aussi au couvreur du Texas.
 * D'où une seule bannière pour tout le monde, plutôt qu'un affichage
 * conditionnel selon le pays — du code fragile pour économiser un clic.
 *
 * La décision est conservée avec sa DATE : sans elle, impossible de démontrer
 * que le consentement a été obtenu, ce qui est justement ce qu'on doit pouvoir
 * prouver. Et avec une VERSION : le jour où la liste des traceurs change, la
 * question doit être reposée — un consentement donné pour Meta ne couvre pas
 * ce qu'on ajouterait ensuite.
 */

const CLE = "rb_consentement";

// À incrémenter dès qu'un traceur est AJOUTÉ (pas quand on en retire un :
// personne n'a besoin d'être reconsulté pour qu'on le suive moins).
//   1 — pixel Meta seul (2026-08-04)
//   2 — ajout de Google Analytics 4 (2026-08-04) : un oui donné pour Meta ne
//       couvre pas Google, donc tout le monde est reconsulté.
export const VERSION_CONSENTEMENT = 2;

/** Décision enregistrée, ou null si le visiteur n'a jamais répondu. */
export function lireConsentement() {
  try {
    const brut = window.localStorage.getItem(CLE);
    if (!brut) return null;
    const doc = JSON.parse(brut);
    if (doc?.version !== VERSION_CONSENTEMENT) return null;  // la liste a changé
    if (doc?.choix !== "oui" && doc?.choix !== "non") return null;
    return doc;
  } catch {
    // Navigation privée stricte : localStorage peut lever. Pas de mémoire
    // possible, donc pas de consentement démontrable, donc pas de pistage.
    return null;
  }
}

/** Vrai seulement si le visiteur a explicitement accepté. */
export function pistageAutorise() {
  return lireConsentement()?.choix === "oui";
}

/** Vrai tant que la question n'a pas été posée (ou que la liste a changé). */
export function consentementARepondre() {
  return lireConsentement() === null;
}

/**
 * Enregistre la décision et prévient l'application.
 * L'évènement évite d'avoir à recharger la page : le pixel démarre (ou reste
 * éteint) dans la seconde qui suit le clic.
 */
export function enregistrerConsentement(choix) {
  const doc = {
    choix: choix === "oui" ? "oui" : "non",
    date: new Date().toISOString(),
    version: VERSION_CONSENTEMENT,
  };
  try {
    window.localStorage.setItem(CLE, JSON.stringify(doc));
  } catch { /* navigation privée : la décision ne tiendra que la visite */ }
  try {
    window.dispatchEvent(new CustomEvent("rb:consentement", { detail: doc }));
  } catch { /* navigateur ancien : le prochain chargement s'en chargera */ }
  return doc;
}

/**
 * Efface la décision pour reposer la question.
 *
 * Se retirer doit être aussi simple que consentir. Le retrait ne peut PAS
 * décharger le pixel déjà en mémoire — le script Meta reste chargé jusqu'au
 * prochain chargement de page — d'où le rechargement déclenché par la
 * bannière quand on passe de « oui » à « non ».
 */
export function oublierConsentement() {
  try {
    window.localStorage.removeItem(CLE);
  } catch { /* rien à effacer */ }
  try {
    window.dispatchEvent(new CustomEvent("rb:consentement", { detail: null }));
  } catch { /* sans effet */ }
}
